import { PaymentType } from '@hunting-nature/shared-core/enums/payment-type.enum';
import type { SdCycle } from '@hunting-nature/shared-core/enums/remote-configs.enum';
import { VideoSize, VideoTransmissionType } from '@hunting-nature/shared-core/enums/remote-configs.enum';
import type { Duration } from '@hunting-nature/shared-core/interfaces/remote-control.interface';
import { PermissionValues } from '@hunting-nature/shared-core/types/permission.type';
import type { SubscriptionPlan } from '@hunting-nature/shared-core/types/subscription-plan.type';
import { SubscriptionPlanValues } from '@hunting-nature/shared-core/types/subscription-plan.type';
import type { Time } from '@hunting-nature/shared-core/types/time.type';
import { createTime } from '@hunting-nature/shared-core/utils/create-time';
import { ServicePlanLevelHandler } from '@hunting-nature/shared-core/utils/service-plan-level-handler';
import type { Action } from '@ngrx/store';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';

import type { VideoSettingsState } from '../../../../../../../libs/shared-core/src/lib/interfaces/video-settings.interface';
import {
  checkIfCameraSupportsDifferentVideoSizes,
  DEFAULT_START_TIME,
  DEFAULT_TIMELAPSE_DURATION,
  formatDateToTimeString,
  getPaymentTypeBySubscription,
  getStatusReportDisplayLabel,
  getStatusReportSliderIndex,
  isSecacam1or3,
  parseTimelapseDuration,
  parseTimerSetting,
  parseTimeStringToDate,
  SETTING_OFF,
  SETTING_ON,
} from '../../../shared/helper/remote-control.helper';
import { RemoteControlResponseMapper } from '../../../shared/mappers/remote-controll-response.mapper';
import { GeneralActions } from '../../../shared/redux/general/general.actions';

import type { CamerasState } from './cameras.reducer';
import { CAMERAS_STORE_KEY, CameraSelectors } from './cameras.reducer';
import { CamerasActions } from './cameras.actions';
import { RemoteControlActions } from './remote-control.actions';
import { SlotManagerActions } from '../../slot-manager/redux/slot-manager.actions';

export const REMOTE_CONTROL_STORE_KEY = 'remote-control';

export interface RemoteControlState {
  cameraId: string | undefined;
  cameraModel: string | undefined;
  loadingRemoteControl: boolean;
  updatingSensitivity: boolean;
  applyingGpsCoordinates: boolean;
  imei: string | undefined;
  cameraName: string | undefined;
  permission: string | undefined;
  subscription: string | undefined;
  cameraMode: string | undefined;
  cameraModeOff: boolean;
  cameraModePending: boolean;
  sensitivity: string | undefined;
  sensitivityPending: boolean;
  nightMode: string | undefined;
  nightModePending: boolean;
  maxNumberPerHour: string | undefined;
  maxNumberPerHourPending: boolean;
  pictureNumberToSend: number | undefined;
  pictureNumberToSendPending: boolean;
  quickPhotoEnabled: boolean;
  quickPhotoEnabledPending: boolean;
  snapshotNumber: number | undefined;
  snapshotNumberPending: boolean;
  imageSize: string | undefined;
  imageSizePending: boolean;
  timerSettingPending: boolean;
  timerSetting: string | undefined;
  timerSettingOff: boolean;

  sendReportInterval: string | undefined;
  sendReportIntervalPending: boolean;
  sendReportIntervalOff: boolean;
  instantMode: string | undefined;
  instantModePending: boolean;
  instantModeOff: boolean;
  gps: string | undefined;
  gpsPending: boolean;
  gpsOff: boolean;
  sdCycle: SdCycle | undefined;
  sdCyclePending: boolean;
  sdCycleOff: boolean;
  pinCodePending: boolean;
  resetPending: boolean;
  paymentType: PaymentType;
  subscriptionPlan: SubscriptionPlan;
  insufficientPermissionDialogVisible: boolean;
  getServicePlanDialogVisible: boolean;
  existingServicePlanSlotDialog: boolean;
  upgradeServicePlanDialogVisible: boolean;
  cameraSlots: number;
  assignedCamerasCount: number;
  // SecaTrack
  loadingSecaTrack: boolean;
  secaTrack: string | undefined;
  secaTrackPending: boolean;
  secaTrackActive: boolean;
  secaTrackOff: boolean;

  // Remote Control settings - Footer Cam Name
  loadingFooterCameraName: boolean;
  cameraNameSetting: string | undefined;
  cameraNameString: string | undefined;
  cameraNameSettingPending: boolean;
  cameraNameSettingOff: boolean;

  // Remote Control settings - Flash Mode
  updatingFlashMode: boolean;
  flashModeError?: string;

  // Remote Control settings - Max Photos
  updatingMaxPhotos: boolean;
  maxPhotosError?: string;
  lowLimitHintShown: boolean;

  // Remote Control settings - Active monitoring time
  updatingActiveMonitoring: boolean;
  activeMonitoringError?: string;

  // Remote Control settings - Pause Capture
  updatingPauseCaptureDelay: boolean;
  pauseCaptureDelayPickerShown: boolean;
  timelapseDurationPickerShown: boolean;
  timePickerContext: 'activeMonitoringStart' | 'activeMonitoringEnd' | 'controlPhoto1' | 'controlPhoto2' | null;
  pauseCaptureDelayEnabled: boolean;
  pauseCaptureDelay: Duration;
  pauseCaptureDelayPending: boolean;

  // Remote Control settings - Always On
  showAlwaysOnConfirmationAlert: boolean;

  // Remote Control settings - Multishot
  updatingMultishot: boolean;

  // Remote Control settings - Quick Photo
  updatingQuickPhoto: boolean;

  // Remote Control settings - Snapshot Number
  updatingSnapshotNumber: boolean;

  // Remote Control settings - Picture Number To Send
  updatingPictureNumberToSend: boolean;

  // Remote Control settings - Sent photo size
  sendingPictureSize: string | undefined;
  sendingPictureSizePending: boolean;
  updatingPhotoSize: boolean;

  // Remote Control settings - Image Size (SD Card)
  updatingImageSize: boolean;

  // Remote Control settings - Video
  videoSize: VideoSize | undefined;
  videoSizePending: boolean;
  videoSizeUpdating: boolean;

  videoSizeSectionEnabled: boolean;

  videoTransmissionEnabled: boolean;
  videoTransmissionPending: boolean;
  videoTransmissionUpdating: boolean;

  videoTransmissionType: VideoTransmissionType | undefined;
  videoTransmissionTypePending: boolean;
  videoTransmissionTypeUpdating: boolean;
  videoTransmissionSectionEnabled: boolean;

  videoRecordingEnabled: boolean;
  videoRecordingPending: boolean;
  videoRecordingUpdating: boolean;

  videoLength: number;
  videoLengthPending: boolean;
  videoLengthUpdating: boolean;
  videoLengthSectionEnabled: boolean;

  // Remote Control settings - Time-lapse
  timelapseSetting: string | undefined;
  motionSensorSetting: string | undefined;
  timelapseSettingPending: boolean;
  timeLapseUpdating: boolean;
  timelapseSettingOff: boolean;

  // Remote Control settings - Control Photos
  applyingControlPhotos: boolean;
  controlPicture1: Time | undefined;
  controlPicture1Pending: boolean;
  controlPicture1Off: boolean;
  controlPicture2: Time | undefined;
  controlPicture2Pending: boolean;
  controlPicture2Off: boolean;

  // Remote Control settings - Status Report
  isUpdatingStatusReport: boolean;
  statusReportPopoverOpen: boolean;

  // Remote Control settings - System Settings Updating
  updatingFooterCamName: boolean;
  updatingAlwaysOn: boolean;
  updatingGps: boolean;
  updatingSecaTrack: boolean;
  updatingSdCycle: boolean;
  updatingPinCode: boolean;
  updatingFactoryReset: boolean;

  activeMonitoringStartTime: Date | undefined;
  activeMonitoringEndTime: Date | undefined;
  timelapseDuration: Duration | undefined;
}

export const initialRemoteControlState: RemoteControlState = {
  cameraId: undefined,
  cameraModel: undefined,
  loadingRemoteControl: false,
  updatingSensitivity: false,
  applyingGpsCoordinates: false,
  imei: undefined,
  cameraName: undefined,
  permission: undefined,
  subscription: undefined,
  cameraMode: undefined,
  cameraModeOff: false,
  cameraModePending: false,
  sensitivity: undefined,
  sensitivityPending: false,
  nightMode: undefined,
  nightModePending: false,
  maxNumberPerHour: undefined,
  maxNumberPerHourPending: false,
  pictureNumberToSend: undefined,
  pictureNumberToSendPending: false,
  quickPhotoEnabled: false,
  quickPhotoEnabledPending: false,
  snapshotNumber: undefined,
  snapshotNumberPending: false,
  imageSize: undefined,
  imageSizePending: false,
  timerSettingPending: false,
  timerSetting: undefined,
  timerSettingOff: true,

  sendReportInterval: undefined,
  sendReportIntervalPending: false,
  sendReportIntervalOff: false,
  instantMode: undefined,
  instantModePending: false,
  instantModeOff: false,
  gps: undefined,
  gpsPending: false,
  gpsOff: false,
  sdCycle: undefined,
  sdCyclePending: false,
  sdCycleOff: false,
  pinCodePending: false,
  resetPending: false,
  paymentType: PaymentType.PREPAID,
  subscriptionPlan: SubscriptionPlanValues.NONE,
  updatingFlashMode: false,
  flashModeError: undefined,

  updatingMaxPhotos: false,
  maxPhotosError: undefined,
  lowLimitHintShown: false,

  updatingActiveMonitoring: false,
  activeMonitoringError: undefined,

  // capture settings: pause capture
  pauseCaptureDelayEnabled: true,
  updatingPauseCaptureDelay: false,
  pauseCaptureDelayPickerShown: false,
  timelapseDurationPickerShown: false,
  timePickerContext: null,
  pauseCaptureDelay: { hours: 0, minutes: 0, seconds: 0 },
  pauseCaptureDelayPending: false,

  showAlwaysOnConfirmationAlert: false,
  updatingMultishot: false,
  updatingQuickPhoto: false,
  updatingSnapshotNumber: false,
  updatingPictureNumberToSend: false,
  updatingPhotoSize: false,
  updatingImageSize: false,
  sendingPictureSize: undefined,
  sendingPictureSizePending: false,
  insufficientPermissionDialogVisible: false,
  getServicePlanDialogVisible: false,
  existingServicePlanSlotDialog: false,
  upgradeServicePlanDialogVisible: false,
  cameraSlots: 0,
  assignedCamerasCount: 0,

  // SecaTrack
  loadingSecaTrack: false,
  secaTrack: undefined,
  secaTrackPending: false,
  secaTrackActive: false,
  secaTrackOff: false,

  // Remote Control settings - Footer Cam Name
  loadingFooterCameraName: false,
  cameraNameSetting: undefined,
  cameraNameString: undefined,
  cameraNameSettingPending: false,
  cameraNameSettingOff: false,

  // Video Settings

  videoSize: undefined,
  videoSizePending: false,
  videoSizeUpdating: false,

  videoTransmissionType: VideoTransmissionType.NoVideoTransmission,
  videoTransmissionTypePending: false,
  videoTransmissionTypeUpdating: false,

  videoTransmissionEnabled: false,
  videoTransmissionPending: false,
  videoTransmissionUpdating: false,

  videoTransmissionSectionEnabled: false,

  videoRecordingEnabled: false,
  videoRecordingPending: false,
  videoRecordingUpdating: false,

  videoSizeSectionEnabled: false,

  videoLength: 10,
  videoLengthPending: false,
  videoLengthUpdating: false,

  videoLengthSectionEnabled: false,
  timelapseSetting: undefined,
  motionSensorSetting: undefined,
  timelapseSettingPending: false,
  timeLapseUpdating: false,
  timelapseSettingOff: false,

  // Remote Control settings - Control Photos
  applyingControlPhotos: false,
  controlPicture1: undefined,
  controlPicture1Pending: false,
  controlPicture1Off: true,
  controlPicture2: undefined,
  controlPicture2Pending: false,
  controlPicture2Off: true,

  isUpdatingStatusReport: false,
  statusReportPopoverOpen: false,

  updatingFooterCamName: false,
  updatingAlwaysOn: false,
  updatingGps: false,
  updatingSecaTrack: false,
  updatingSdCycle: false,
  updatingPinCode: false,
  updatingFactoryReset: false,

  activeMonitoringStartTime: undefined,
  activeMonitoringEndTime: undefined,
  timelapseDuration: undefined,
};

declare const window: Window &
  typeof globalThis & {
    Cypress: unknown;
    initialState: {
      remoteControl: RemoteControlState;
    };
  };

const initialState: RemoteControlState =
  window.Cypress && window?.initialState?.remoteControl ? window?.initialState?.remoteControl : initialRemoteControlState;

const myReducer = createReducer(
  initialState,
  on(CamerasActions.editCameraName, (state: RemoteControlState, action): RemoteControlState => {
    return {
      ...state,
      cameraName: action.cameraName,
    };
  }),

  on(CamerasActions.editCameraNameFailed, (state: RemoteControlState, action): RemoteControlState => {
    return {
      ...state,
      cameraName: action.originalCameraName,
    };
  }),

  on(GeneralActions.goToRemoteControl, (state: RemoteControlState, action): RemoteControlState => {
    return {
      ...state,
      loadingRemoteControl: true,
      imei: action.imei,
      cameraId: action.cameraId,
      cameraName: action.name,
      cameraModel: action.model,
      permission: action.permission,
      subscription: action.subscription,
      paymentType: getPaymentTypeBySubscription(action.subscription),
    };
  }),

  on(RemoteControlActions.enterRemoteControlPage, (state: RemoteControlState, action): RemoteControlState => {
    const isCameraChanging = !!action.cameraId && action.cameraId !== state.cameraId;

    return {
      ...state,
      loadingRemoteControl: true,
      cameraId: action.cameraId ?? state.cameraId,
      cameraModel: isCameraChanging ? undefined : state.cameraModel,
      imei: action.imei,
      timePickerContext: null,
    };
  }),
  on(RemoteControlActions.enterRemoteControlPageFailed, (state: RemoteControlState, _action): RemoteControlState => {
    return {
      ...state,
      loadingRemoteControl: false,
    };
  }),
  on(RemoteControlActions.enterRemoteControlPageSucceeded, (state: RemoteControlState, action): RemoteControlState => {
    return {
      ...state,
      loadingRemoteControl: false,
      cameraSlots: action.subscription?.cameraSlots ?? 0,
      assignedCamerasCount: action.subscription?.assignedCamerasCount ?? 0,
      ...RemoteControlResponseMapper.toPartialRemoteControlReduxState(action.remoteControl),
      cameraName: action.camera?.name ?? state.cameraName,
      cameraModel: action.camera?.model ?? state.cameraModel,
      permission: action.camera?.permission ?? state.permission,
      subscription: action.camera?.subscriptionPlan ?? state.subscription,
      ...getCurrentVideoSettingsState(action.videoSettings, action.camera?.model ?? state.cameraModel ?? ''),
    };
  }),

  on(RemoteControlActions.fetchRemoteControlStatus, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      loadingRemoteControl: true,
    };
  }),
  on(RemoteControlActions.fetchRemoteControlStatusFailed, (state: RemoteControlState, _action): RemoteControlState => {
    return {
      ...state,
      loadingRemoteControl: false,
    };
  }),
  on(RemoteControlActions.fetchRemoteControlStatusSucceeded, (state: RemoteControlState, action): RemoteControlState => {
    return {
      ...state,
      loadingRemoteControl: false,
      cameraSlots: action.subscription?.cameraSlots ?? 0,
      assignedCamerasCount: action.subscription?.assignedCamerasCount ?? 0,
      ...RemoteControlResponseMapper.toPartialRemoteControlReduxState(action.data),
    };
  }),
  on(RemoteControlActions.updateSensitivity, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      updatingSensitivity: true,
    };
  }),
  on(RemoteControlActions.updateSensitivitySucceeded, (state: RemoteControlState, action): RemoteControlState => {
    return {
      ...state,
      updatingSensitivity: false,
      sensitivityPending: true,
    };
  }),
  on(RemoteControlActions.updateSensitivityFailed, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      updatingSensitivity: false,
    };
  }),
  on(RemoteControlActions.applyImageSizeCard, (state: RemoteControlState, action): RemoteControlState => {
    return {
      ...state,
      imageSizePending: true,
      imageSize: action.photoSize,
      updatingImageSize: true,
    };
  }),
  on(RemoteControlActions.applyImageSizeSucceeded, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      updatingImageSize: false,
    };
  }),
  on(RemoteControlActions.applyImageSizeFailed, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      updatingImageSize: false,
    };
  }),
  on(RemoteControlActions.enterSecaTrackPage, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      loadingSecaTrack: true,
    };
  }),
  on(RemoteControlActions.enterSecaTrackPageSucceeded, (state: RemoteControlState, action): RemoteControlState => {
    return {
      ...state,
      ...(action.data ? RemoteControlResponseMapper.toPartialRemoteControlReduxState(action.data) : undefined),
      loadingSecaTrack: false,
    };
  }),
  on(RemoteControlActions.enterSecaTrackPageFailed, (state: RemoteControlState, _action): RemoteControlState => {
    return {
      ...state,
      loadingSecaTrack: false,
    };
  }),
  on(RemoteControlActions.applyGpsPulse, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      loadingSecaTrack: true,
    };
  }),
  on(RemoteControlActions.applyGpsPulseSucceeded, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      loadingSecaTrack: false,
    };
  }),
  on(RemoteControlActions.applyGpsPulseFailed, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      loadingSecaTrack: false,
    };
  }),
  on(RemoteControlActions.applyPictureNumberToSendPending, (state: RemoteControlState, action): RemoteControlState => {
    return {
      ...state,
      pictureNumberToSendPending: true,
      pictureNumberToSend: action.pictureNumberToSend,
      updatingPictureNumberToSend: true,
    };
  }),
  on(RemoteControlActions.applyPictureNumberToSendPendingSucceeded, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      updatingPictureNumberToSend: false,
    };
  }),
  on(RemoteControlActions.applyPictureNumberToSendPendingFailed, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      updatingPictureNumberToSend: false,
    };
  }),

  on(RemoteControlActions.enterFooterCamNamePage, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      loadingFooterCameraName: true,
    };
  }),
  on(RemoteControlActions.enterFooterCamNamePageSucceeded, (state: RemoteControlState, action): RemoteControlState => {
    return {
      ...state,
      ...(action.data ? RemoteControlResponseMapper.toPartialRemoteControlReduxState(action.data) : undefined),
      loadingFooterCameraName: false,
    };
  }),
  on(RemoteControlActions.enterFooterCamNamePageFailed, (state: RemoteControlState, _action): RemoteControlState => {
    return {
      ...state,
      loadingFooterCameraName: false,
    };
  }),

  on(RemoteControlActions.applyFooterCamName, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      loadingFooterCameraName: true,
    };
  }),
  on(RemoteControlActions.applyFooterCamNameSucceeded, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      loadingFooterCameraName: false,
    };
  }),
  on(RemoteControlActions.applyFooterCamNameFailed, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      loadingFooterCameraName: false,
    };
  }),
  on(RemoteControlActions.applyGpsCoordinates, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      applyingGpsCoordinates: true,
    };
  }),
  on(RemoteControlActions.applyGpsCoordinatesSucceeded, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      applyingGpsCoordinates: false,
    };
  }),
  on(RemoteControlActions.applyGpsCoordinatesFailed, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      applyingGpsCoordinates: false,
    };
  }),
  on(RemoteControlActions.updateFlashMode, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      updatingFlashMode: true,
      flashModeError: undefined,
    };
  }),
  on(RemoteControlActions.updateFlashModeSucceeded, (state: RemoteControlState, action): RemoteControlState => {
    return {
      ...state,
      updatingFlashMode: false,
      flashModeError: undefined,
      nightModePending: true,
    };
  }),
  on(RemoteControlActions.updateFlashModeFailed, (state: RemoteControlState, action): RemoteControlState => {
    return {
      ...state,
      updatingFlashMode: false,
      flashModeError: action.error.message,
    };
  }),
  on(RemoteControlActions.updateMaxPhotos, (state: RemoteControlState, action): RemoteControlState => {
    return {
      ...state,
      updatingMaxPhotos: true,
      maxPhotosError: undefined,
      lowLimitHintShown: Number.parseInt(action.maxPhotos) < 10,
    };
  }),
  on(RemoteControlActions.updateMaxPhotosSucceeded, (state: RemoteControlState, action): RemoteControlState => {
    return {
      ...state,
      updatingMaxPhotos: false,
      maxPhotosError: undefined,
      maxNumberPerHourPending: true,
    };
  }),
  on(RemoteControlActions.updateMaxPhotosFailed, (state: RemoteControlState, action): RemoteControlState => {
    return {
      ...state,
      updatingMaxPhotos: false,
      maxPhotosError: action.error.message,
    };
  }),
  on(RemoteControlActions.updatePauseCapture, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      updatingPauseCaptureDelay: true,
      pauseCaptureDelayPickerShown: false,
    };
  }),
  on(RemoteControlActions.updatePauseCaptureSucceeded, (state: RemoteControlState, action): RemoteControlState => {
    return {
      ...state,
      updatingPauseCaptureDelay: false,
      pauseCaptureDelay: action.duration,
      pauseCaptureDelayPickerShown: false,
      pauseCaptureDelayPending: true,
    };
  }),
  on(RemoteControlActions.updatePauseCaptureFailed, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      updatingPauseCaptureDelay: false,
      pauseCaptureDelayPickerShown: false,
    };
  }),
  on(
    RemoteControlActions.updateActiveMonitoring,
    (state, action): RemoteControlState => ({
      ...state,
      updatingActiveMonitoring: true,
      activeMonitoringError: undefined,
      timerSetting: action.timerSetting,
      timerSettingOff: action.timerSetting === SETTING_OFF,
      timerSettingPending: true,
      activeMonitoringStartTime: action.timerSetting === SETTING_OFF ? undefined : parseTimerSetting(action.timerSetting).startTime,
      activeMonitoringEndTime: action.timerSetting === SETTING_OFF ? undefined : parseTimerSetting(action.timerSetting).endTime,
      timePickerContext: null,
    })
  ),
  on(
    RemoteControlActions.updateActiveMonitoringSucceeded,
    (state): RemoteControlState => ({
      ...state,
      updatingActiveMonitoring: false,
      timePickerContext: null,
    })
  ),
  on(
    RemoteControlActions.updateActiveMonitoringFailed,
    (state, action): RemoteControlState => ({
      ...state,
      updatingActiveMonitoring: false,
      activeMonitoringError: action.error.message,
      timePickerContext: null,
    })
  ),
  on(RemoteControlActions.setInstantMode, (state: RemoteControlState, action): RemoteControlState => {
    return {
      ...state,
      instantMode: action.mode,
      instantModeOff: action.mode === '0',
    };
  }),
  on(RemoteControlActions.showAlwaysOnConfirmationAlert, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      showAlwaysOnConfirmationAlert: true,
    };
  }),
  on(RemoteControlActions.hideAlwaysOnConfirmationAlert, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      showAlwaysOnConfirmationAlert: false,
    };
  }),
  on(RemoteControlActions.enterAlwaysOnPage, (state: RemoteControlState): RemoteControlState => {
    return {
      ...state,
      showAlwaysOnConfirmationAlert: false,
    };
  }),
  on(
    RemoteControlActions.activateQuickPhotoMode,
    (state, _action): RemoteControlState => ({
      ...state,
      quickPhotoEnabledPending: true,
      quickPhotoEnabled: true,
      updatingQuickPhoto: true,
    })
  ),
  on(
    RemoteControlActions.deactivateQuickPhotoMode,
    (state, _action): RemoteControlState => ({
      ...state,
      quickPhotoEnabledPending: true,
      quickPhotoEnabled: false,
      updatingQuickPhoto: true,
    })
  ),

  on(
    RemoteControlActions.activateQuickPhotoModeSucceeded,
    RemoteControlActions.deactivateQuickPhotoModeSucceeded,
    (state): RemoteControlState => ({
      ...state,
      updatingQuickPhoto: false,
    })
  ),
  on(
    RemoteControlActions.activateQuickPhotoModeFailed,
    RemoteControlActions.deactivateQuickPhotoModeFailed,
    (state): RemoteControlState => ({
      ...state,
      updatingQuickPhoto: false,
    })
  ),

  on(
    RemoteControlActions.applySnapshotNumber,
    (state, action): RemoteControlState => ({
      ...state,
      snapshotNumberPending: true,
      snapshotNumber: action.snapshotNumber,
      pictureNumberToSendPending: action.snapshotNumber < Number(state.pictureNumberToSend),
      pictureNumberToSend: Math.min(action.snapshotNumber, Number(state.pictureNumberToSend)),
      updatingSnapshotNumber: true,
    })
  ),
  on(
    RemoteControlActions.applySnapshotNumberSucceeded,
    (state): RemoteControlState => ({
      ...state,
      updatingSnapshotNumber: false,
    })
  ),
  on(
    RemoteControlActions.applySnapshotNumberFailed,
    (state): RemoteControlState => ({
      ...state,
      updatingSnapshotNumber: false,
    })
  ),
  on(
    RemoteControlActions.applySendingPictureSize,
    (state, action): RemoteControlState => ({
      ...state,
      sendingPictureSizePending: true,
      sendingPictureSize: action.photoSize,
      updatingPhotoSize: true,
    })
  ),
  on(
    RemoteControlActions.applySendingPictureSizeSucceeded,
    (state): RemoteControlState => ({
      ...state,
      updatingPhotoSize: false,
    })
  ),
  on(
    RemoteControlActions.applySendingPictureSizeFailed,
    (state): RemoteControlState => ({
      ...state,
      updatingPhotoSize: false,
    })
  ),

  on(
    RemoteControlActions.enableVideoTransmission,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      videoTransmissionEnabled: true,
      videoTransmissionSectionEnabled: true,

      videoSize: VideoSize.Size_480P, // set default video size (480P) when enabling video transmission
      videoSizeSectionEnabled: false,

      videoLength: 10, // set default video length to 10sec. when enabling video transmission
      videoLengthSectionEnabled: false,

      // set default video transmission type (OnDemand) if no type is set
      videoTransmissionType:
        state.videoTransmissionType == VideoTransmissionType.NoVideoTransmission ? VideoTransmissionType.OnDemand : state.videoTransmissionType,

      videoTransmissionPending: true,
      videoTransmissionUpdating: true,
    })
  ),
  on(
    RemoteControlActions.enableVideoTransmissionSucceeded,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,

      videoTransmissionUpdating: false,
    })
  ),
  on(
    RemoteControlActions.enableVideoTransmissionFailed,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,

      videoTransmissionUpdating: false,
    })
  ),
  on(
    RemoteControlActions.changeVideoSize,
    (state: RemoteControlState, action): RemoteControlState => ({
      ...state,
      videoSize: action.settings.videoSize!,

      videoSizePending: true,
      videoSizeUpdating: true,
    })
  ),
  on(
    RemoteControlActions.changeVideoSizeSucceeded,
    (state: RemoteControlState, action): RemoteControlState => ({
      ...state,

      videoSizeUpdating: false,
    })
  ),
  on(
    RemoteControlActions.changeVideoSizeFailed,
    (state: RemoteControlState, action): RemoteControlState => ({
      ...state,

      videoSizePending: false,
      videoSizeUpdating: false,
    })
  ),
  on(
    RemoteControlActions.changeVideoLength,
    (state: RemoteControlState, action): RemoteControlState => ({
      ...state,
      videoLength: action.settings.videoLength!,
      videoLengthPending: true,
      videoLengthUpdating: true,
    })
  ),
  on(
    RemoteControlActions.changeVideoLengthSucceeded,
    (state: RemoteControlState, action): RemoteControlState => ({
      ...state,
      videoLengthUpdating: false,
    })
  ),
  on(
    RemoteControlActions.changeVideoLengthFailed,
    (state: RemoteControlState, action): RemoteControlState => ({
      ...state,
      videoLengthUpdating: false,
    })
  ),

  on(
    RemoteControlActions.changeVideoTransmissionType,
    (state: RemoteControlState, action): RemoteControlState => ({
      ...state,
      videoTransmissionType: action.settings.videoTransmissionType!,

      videoTransmissionTypePending: true,
      videoTransmissionTypeUpdating: true,
    })
  ),
  on(
    RemoteControlActions.changeVideoTransmissionTypeSucceeded,
    (state: RemoteControlState, action): RemoteControlState => ({
      ...state,
      videoTransmissionTypeUpdating: false,
    })
  ),
  on(
    RemoteControlActions.changeVideoTransmissionTypeFailed,
    (state: RemoteControlState, action): RemoteControlState => ({
      ...state,
      videoTransmissionTypePending: false,
      videoTransmissionTypeUpdating: false,
    })
  ),

  on(
    RemoteControlActions.disableVideoTransmission,
    (state: RemoteControlState, _action): RemoteControlState => ({
      ...state,
      videoTransmissionEnabled: false,
      videoTransmissionSectionEnabled: false,

      videoSizeSectionEnabled: checkIfCameraSupportsDifferentVideoSizes(state.cameraModel) && state.videoRecordingEnabled,
      videoLengthSectionEnabled: state.videoRecordingEnabled,

      videoTransmissionPending: true,
      videoTransmissionUpdating: true,
    })
  ),
  on(
    RemoteControlActions.disableVideoTransmissionSucceeded,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      videoTransmissionUpdating: false,
    })
  ),
  on(
    RemoteControlActions.disableVideoTransmissionFailed,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      videoTransmissionUpdating: false,
    })
  ),
  on(
    RemoteControlActions.enableVideoRecording,
    (state: RemoteControlState, _action): RemoteControlState => ({
      ...state,
      videoRecordingEnabled: true,
      videoRecordingPending: true,
      videoRecordingUpdating: true,
      videoLengthSectionEnabled: !state.videoTransmissionEnabled,
      videoSizeSectionEnabled: !state.videoTransmissionEnabled && checkIfCameraSupportsDifferentVideoSizes(state.cameraModel),
    })
  ),
  on(
    RemoteControlActions.enableVideoRecordingSucceeded,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      videoRecordingUpdating: false,
    })
  ),
  on(
    RemoteControlActions.enableVideoRecordingFailed,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      videoRecordingUpdating: false,
    })
  ),
  on(
    RemoteControlActions.disableVideoRecording,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      videoRecordingEnabled: false,
      videoSizeSectionEnabled: false,
      videoLengthSectionEnabled: false,
      videoRecordingPending: true,
      videoRecordingUpdating: true,
    })
  ),
  on(
    RemoteControlActions.disableVideoRecordingSucceeded,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,

      videoRecordingUpdating: false,
    })
  ),
  on(
    RemoteControlActions.disableVideoRecordingFailed,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,

      videoRecordingPending: false,
      videoRecordingUpdating: false,
    })
  ),
  on(
    RemoteControlActions.applyTimeLapseSettings,
    (state, action): RemoteControlState => ({
      ...state,
      timeLapseUpdating: true,
      timelapseSetting: action.timelapseSetting,
      timelapseSettingOff: action.timelapseSetting === SETTING_OFF,
      motionSensorSetting: action.motionSensorSetting,
      timelapseDuration: action.timelapseSetting === SETTING_OFF ? undefined : parseTimelapseDuration(action.timelapseSetting),
      timelapseDurationPickerShown: false,
    })
  ),
  on(
    RemoteControlActions.applyTimeLapseSettingsSucceeded,
    (state): RemoteControlState => ({
      ...state,
      timeLapseUpdating: false,
      timelapseSettingPending: true,
      timelapseDurationPickerShown: false,
    })
  ),
  on(
    RemoteControlActions.applyTimeLapseSettingsFailed,
    (state): RemoteControlState => ({
      ...state,
      timeLapseUpdating: false,
      timelapseDurationPickerShown: false,
    })
  ),
  on(
    RemoteControlActions.applyControlPhotos,
    (state, action): RemoteControlState => ({
      ...state,
      applyingControlPhotos: true,
      controlPicture1: action.timer1 ? createTime(formatDateToTimeString(action.timer1)) : undefined,
      controlPicture1Off: action.timer1 === undefined,
      controlPicture1Pending: true,
      controlPicture2: action.timer2 ? createTime(formatDateToTimeString(action.timer2)) : undefined,
      controlPicture2Off: action.timer2 === undefined,
      controlPicture2Pending: true,
      timePickerContext: null,
    })
  ),
  on(
    RemoteControlActions.applyControlPhotosSucceeded,
    (state): RemoteControlState => ({
      ...state,
      applyingControlPhotos: false,
      timePickerContext: null,
    })
  ),
  on(
    RemoteControlActions.applyControlPhotosFailed,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      applyingControlPhotos: false,
      timePickerContext: null,
    })
  ),
  on(
    RemoteControlActions.applySdCycle,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      sdCyclePending: true,
    })
  ),
  on(
    RemoteControlActions.applySdCycleSucceeded,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      sdCyclePending: true,
    })
  ),
  on(
    RemoteControlActions.applySdCycleFailed,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      sdCyclePending: false,
    })
  ),

  // Reset PIN Code
  on(
    RemoteControlActions.applyResetPin,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      pinCodePending: true,
      updatingPinCode: true,
    })
  ),
  on(
    RemoteControlActions.applyResetPinSucceeded,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      pinCodePending: true,
      updatingPinCode: false,
    })
  ),
  on(
    RemoteControlActions.applyResetPinFailed,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      pinCodePending: false,
      updatingPinCode: false,
    })
  ),

  // Factory Reset
  on(
    RemoteControlActions.applyCameraResetFactorySettings,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      resetPending: true,
      updatingFactoryReset: true,
    })
  ),
  on(
    RemoteControlActions.applyCameraResetFactorySettingsSucceeded,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      resetPending: true,
      updatingFactoryReset: false,
    })
  ),
  on(
    RemoteControlActions.applyCameraResetFactorySettingsFailed,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      resetPending: false,
      updatingFactoryReset: false,
    })
  ),

  on(
    RemoteControlActions.openInsufficientPermissionDialog,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      insufficientPermissionDialogVisible: true,
    })
  ),
  on(
    RemoteControlActions.closeInsufficientPermissionDialog,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      insufficientPermissionDialogVisible: false,
    })
  ),
  on(
    RemoteControlActions.dismissInsufficientPermissionDialog,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      insufficientPermissionDialogVisible: false,
    })
  ),
  on(
    RemoteControlActions.acceptInsufficientPermission,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      insufficientPermissionDialogVisible: false,
    })
  ),

  on(
    RemoteControlActions.openNotPartOfServicePlanDialog,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      getServicePlanDialogVisible: true,
    })
  ),
  on(
    RemoteControlActions.cancelGetServicePlan,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      getServicePlanDialogVisible: false,
    })
  ),
  on(
    RemoteControlActions.closeNotPartOfServicePlanDialog,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      getServicePlanDialogVisible: false,
    })
  ),
  on(
    RemoteControlActions.dismissNotPartOfServicePlanDialog,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      getServicePlanDialogVisible: false,
    })
  ),
  on(
    RemoteControlActions.getServicePlan,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      getServicePlanDialogVisible: false,
    })
  ),

  on(
    RemoteControlActions.openExistingServicePlanSlotDialog,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      existingServicePlanSlotDialog: true,
    })
  ),
  on(
    RemoteControlActions.cancelAddCameraToServicePlan,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      existingServicePlanSlotDialog: false,
    })
  ),
  on(
    RemoteControlActions.closeExistingServicePlanSlotDialog,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      existingServicePlanSlotDialog: false,
    })
  ),
  on(
    RemoteControlActions.dismissExistingServicePlanSlotDialog,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      existingServicePlanSlotDialog: false,
    })
  ),
  on(
    RemoteControlActions.addCameraToServicePlan,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      existingServicePlanSlotDialog: false,
    })
  ),

  on(
    RemoteControlActions.openUpgradeServicePlanDialog,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      upgradeServicePlanDialogVisible: true,
    })
  ),
  on(
    RemoteControlActions.cancelUpgradeServicePlan,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      upgradeServicePlanDialogVisible: false,
    })
  ),
  on(
    RemoteControlActions.closeUpgradeServicePlanDialog,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      upgradeServicePlanDialogVisible: false,
    })
  ),
  on(
    RemoteControlActions.dismissUpgradeServicePlanDialog,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      upgradeServicePlanDialogVisible: false,
    })
  ),
  on(
    RemoteControlActions.upgradeServicePlan,
    (state: RemoteControlState): RemoteControlState => ({
      ...state,
      upgradeServicePlanDialogVisible: false,
    })
  ),

  on(
    RemoteControlActions.applyStatusReport,
    (state, action): RemoteControlState => ({
      ...state,
      isUpdatingStatusReport: true,
      sendReportInterval: action.interval,
      sendReportIntervalPending: true,
      statusReportPopoverOpen: false, // Close popover when applying
    })
  ),
  on(
    RemoteControlActions.applyStatusReportSucceeded,
    (state): RemoteControlState => ({
      ...state,
      isUpdatingStatusReport: false,
    })
  ),
  on(
    RemoteControlActions.applyStatusReportFailed,
    (state, _action): RemoteControlState => ({
      ...state,
      isUpdatingStatusReport: false,
    })
  ),

  // Status Report Popover state
  on(
    RemoteControlActions.openStatusReportPopover,
    (state): RemoteControlState => ({
      ...state,
      statusReportPopoverOpen: true,
    })
  ),
  on(
    RemoteControlActions.closeStatusReportPopover,
    (state): RemoteControlState => ({
      ...state,
      statusReportPopoverOpen: false,
    })
  ),
  on(
    RemoteControlActions.setLocalFooterCamName,
    (state, action): RemoteControlState => ({
      ...state,
      cameraNameString: action.footerCamName,
    })
  ),
  on(
    RemoteControlActions.setLocalAlwaysOn,
    (state, action): RemoteControlState => ({
      ...state,
      instantModeOff: !action.enabled,
      instantModePending: true,
      updatingAlwaysOn: true,
    })
  ),
  on(
    RemoteControlActions.setLocalGps,
    (state, action): RemoteControlState => ({
      ...state,
      gpsOff: !action.enabled,
      gpsPending: true,
      updatingGps: true,
    })
  ),
  on(
    RemoteControlActions.setLocalSecaTrack,
    (state, action): RemoteControlState => ({
      ...state,
      secaTrackOff: !action.enabled,
      secaTrackPending: true,
      updatingSecaTrack: true,
    })
  ),
  on(
    RemoteControlActions.setLocalSdCycle,
    (state, action): RemoteControlState => ({
      ...state,
      sdCycleOff: !action.enabled,
      sdCyclePending: true,
      updatingSdCycle: true,
    })
  ),

  on(
    RemoteControlActions.saveFooterCamName,
    (state): RemoteControlState => ({
      ...state,
      cameraNameSettingPending: true,
      updatingFooterCamName: true,
    })
  ),
  on(
    RemoteControlActions.saveFooterCamNameSucceeded,
    (state, action): RemoteControlState => ({
      ...state,
      ...RemoteControlResponseMapper.toPartialRemoteControlReduxState(action.state),
      updatingFooterCamName: false,
    })
  ),
  on(
    RemoteControlActions.saveFooterCamNameFailed,
    (state): RemoteControlState => ({
      ...state,
      updatingFooterCamName: false,
    })
  ),
  on(
    RemoteControlActions.saveAlwaysOnSucceeded,
    (state, action): RemoteControlState => ({
      ...state,
      ...RemoteControlResponseMapper.toPartialRemoteControlReduxState(action.state),
      updatingAlwaysOn: false,
    })
  ),
  on(
    RemoteControlActions.saveAlwaysOnFailed,
    (state): RemoteControlState => ({
      ...state,
      updatingAlwaysOn: false,
    })
  ),
  on(
    RemoteControlActions.saveGpsSucceeded,
    (state, action): RemoteControlState => ({
      ...state,
      ...RemoteControlResponseMapper.toPartialRemoteControlReduxState(action.state),
      updatingGps: false,
    })
  ),
  on(
    RemoteControlActions.saveGpsFailed,
    (state): RemoteControlState => ({
      ...state,
      updatingGps: false,
    })
  ),
  on(
    RemoteControlActions.saveSecaTrackSucceeded,
    (state, action): RemoteControlState => ({
      ...state,
      ...RemoteControlResponseMapper.toPartialRemoteControlReduxState(action.state),
      updatingSecaTrack: false,
    })
  ),
  on(
    RemoteControlActions.saveSecaTrackFailed,
    (state): RemoteControlState => ({
      ...state,
      updatingSecaTrack: false,
    })
  ),
  on(
    RemoteControlActions.saveSdCycleSucceeded,
    (state, action): RemoteControlState => ({
      ...state,
      ...RemoteControlResponseMapper.toPartialRemoteControlReduxState(action.state),
      updatingSdCycle: false,
    })
  ),
  on(
    RemoteControlActions.saveSdCycleFailed,
    (state): RemoteControlState => ({
      ...state,
      updatingSdCycle: false,
    })
  ),
  on(RemoteControlActions.showPauseCaptureDelayPicker, (state): RemoteControlState => {
    return {
      ...state,
      pauseCaptureDelayPickerShown: true,
    };
  }),
  on(RemoteControlActions.hidePauseCaptureDelayPicker, (state): RemoteControlState => {
    return {
      ...state,
      pauseCaptureDelayPickerShown: false,
    };
  }),
  on(RemoteControlActions.showTimelapseDurationPicker, (state): RemoteControlState => {
    return {
      ...state,
      timelapseDurationPickerShown: true,
    };
  }),
  on(RemoteControlActions.hideTimelapseDurationPicker, (state): RemoteControlState => {
    return {
      ...state,
      timelapseDurationPickerShown: false,
    };
  }),
  on(
    RemoteControlActions.openTimePicker,
    (state, { context }): RemoteControlState => ({
      ...state,
      timePickerContext: context,
    })
  ),
  on(
    RemoteControlActions.hideTimePicker,
    (state): RemoteControlState => ({
      ...state,
      timePickerContext: null,
    })
  ),
  on(RemoteControlActions.activatePauseCapture, (state): RemoteControlState => {
    return {
      ...state,
      pauseCaptureDelayEnabled: true,
      updatingPauseCaptureDelay: true,
    };
  }),
  on(RemoteControlActions.activatePauseCaptureSucceeded, (state): RemoteControlState => {
    return {
      ...state,
      updatingPauseCaptureDelay: false,
    };
  }),
  on(RemoteControlActions.activatePauseCaptureFailed, (state): RemoteControlState => {
    return {
      ...state,
      updatingPauseCaptureDelay: false,
    };
  }),
  on(RemoteControlActions.deactivatePauseCapture, (state): RemoteControlState => {
    return {
      ...state,
      pauseCaptureDelayEnabled: false,
      updatingPauseCaptureDelay: true,
    };
  }),
  on(RemoteControlActions.deactivatePauseCaptureSucceeded, (state): RemoteControlState => {
    return {
      ...state,
      updatingPauseCaptureDelay: false,
    };
  }),
  on(RemoteControlActions.deactivatePauseCaptureFailed, (state): RemoteControlState => {
    return {
      ...state,
      updatingPauseCaptureDelay: false,
    };
  }),
  on(SlotManagerActions.assignCameraSucceeded, (state): RemoteControlState => {
    return {
      ...state,
      cameraSlots: state.cameraSlots + 1,
      paymentType: PaymentType.SUBSCRIBED,
    };
  })
);

export const remoteControlReducer = (state: RemoteControlState = initialState, action: Action): RemoteControlState => myReducer(state, action);

const remoteControlState = createFeatureSelector<RemoteControlState>(REMOTE_CONTROL_STORE_KEY);
const camerasState = createFeatureSelector<CamerasState>(CAMERAS_STORE_KEY);

const isCameraLegacy = createSelector(remoteControlState, (state: RemoteControlState) => !state?.cameraModel?.toUpperCase().startsWith('ZEISS'));

export const RemoteControlSelectors = {
  loadingRemoteControl: createSelector(
    remoteControlState,
    camerasState,
    (remote: RemoteControlState, camera: CamerasState) => remote.loadingRemoteControl || camera.loadingCameraDetails
  ),
  updatingSensitivity: createSelector(
    remoteControlState,
    camerasState,
    (remote: RemoteControlState, camera: CamerasState) => remote.loadingRemoteControl || remote.updatingSensitivity || camera.loadingCameraDetails
  ),
  loadingGpsCoordinates: createSelector(
    remoteControlState,
    camerasState,
    (remote: RemoteControlState, camera: CamerasState) => remote.loadingRemoteControl || remote.applyingGpsCoordinates || camera.loadingCameraDetails
  ),
  isLoadingSecaTrack: createSelector(remoteControlState, (remote: RemoteControlState) => remote.loadingRemoteControl || remote.loadingSecaTrack),
  isLoadingFooterCamName: createSelector(remoteControlState, (remote: RemoteControlState) => remote.loadingFooterCameraName),
  imei: createSelector(remoteControlState, (state: RemoteControlState) => state.imei),
  cameraId: createSelector(remoteControlState, (state: RemoteControlState) => state.cameraId),
  cameraName: createSelector(remoteControlState, (state: RemoteControlState) => state.cameraName),
  cameraModel: createSelector(remoteControlState, (state: RemoteControlState) => state.cameraModel),
  permission: createSelector(remoteControlState, (state: RemoteControlState) => state.permission),
  subscription: createSelector(remoteControlState, (state: RemoteControlState) => state.subscription),

  prepaid: createSelector(remoteControlState, (state: RemoteControlState) => state.paymentType === PaymentType.PREPAID),
  subscribed: createSelector(remoteControlState, (state: RemoteControlState) => state.paymentType === PaymentType.SUBSCRIBED),

  isCameraViewer: createSelector(remoteControlState, (state: RemoteControlState) => state.permission === PermissionValues.VIEWER),
  isCameraOperator: createSelector(remoteControlState, (state: RemoteControlState) => state.permission === PermissionValues.OPERATOR),
  isCameraOwner: createSelector(remoteControlState, (state: RemoteControlState) => state.permission === PermissionValues.OWNER),
  isCameraOwnerOrOperator: createSelector(
    remoteControlState,
    (state: RemoteControlState) => state.permission === PermissionValues.OWNER || state.permission === PermissionValues.OPERATOR
  ),
  isCameraShared: createSelector(remoteControlState, (state: RemoteControlState) => state.permission !== PermissionValues.OWNER),

  isCameraLegacy,

  isPaywallVisible: createSelector(
    isCameraLegacy,
    remoteControlState,
    (isLegacy: boolean, state: RemoteControlState): boolean =>
      state.paymentType === PaymentType.PREPAID && state.permission === PermissionValues.OWNER && !isLegacy
  ),

  isRenameEnabled: createSelector(
    remoteControlState,
    camerasState,
    (remote: RemoteControlState, camera: CamerasState): boolean =>
      remote.permission === PermissionValues.OWNER && !remote.loadingRemoteControl && !camera.loadingCameraDetails
  ),

  settingsAvailable: createSelector(
    (state: RemoteControlState): boolean => RemoteControlSelectors.isCameraLegacy(state),
    (isLegacy: boolean): boolean => !isLegacy
  ),

  areRestSettingsDisabled: createSelector(
    // The rest settings means all of them except rename-camera, video-settings, reset-pin-code
    (state: RemoteControlState): boolean => RemoteControlSelectors.isCameraOwner(state),
    (state: RemoteControlState): boolean => RemoteControlSelectors.isCameraOperator(state),
    (state: RemoteControlState): boolean => RemoteControlSelectors.isCameraViewer(state),
    (state: RemoteControlState): boolean => RemoteControlSelectors.prepaid(state),
    (isOwner: boolean, isOperator: boolean, isViewer: boolean, isPrepaid: boolean): boolean => {
      return isViewer || (isOperator && isPrepaid) || (isOwner && isPrepaid);
    }
  ),
  isVideoSettingsDisabled: createSelector(
    (state: RemoteControlState): boolean => RemoteControlSelectors.isCameraViewer(state),
    (isViewer: boolean): boolean => isViewer
  ),
  isResetPinCodeDisabled: createSelector(
    (state: RemoteControlState): boolean => RemoteControlSelectors.isVideoSettingsDisabled(state),
    (isVideoSettingsDisabled: boolean): boolean => isVideoSettingsDisabled
  ),

  isDeleteButtonShown: createSelector(
    (state: RemoteControlState): boolean => RemoteControlSelectors.isCameraOwner(state),
    (isOwner: boolean): boolean => {
      return isOwner;
    }
  ),

  atLeastBasicServicePlan: createSelector(remoteControlState, (state: RemoteControlState) => ServicePlanLevelHandler.isAtLeastBasic(state.subscriptionPlan)),
  hasSaveRight: createSelector(remoteControlState, (remote: RemoteControlState) => {
    const isOwnerOrOperator = remote.permission === PermissionValues.OWNER || remote.permission === PermissionValues.OPERATOR;
    return isOwnerOrOperator && ServicePlanLevelHandler.isAtLeastBasic(remote.subscriptionPlan);
  }),
  isPrepaidOrViewer: createSelector(remoteControlState, (remote: RemoteControlState) => {
    const isCameraViewer = remote.permission === PermissionValues.VIEWER;
    return remote.paymentType === PaymentType.PREPAID || isCameraViewer;
  }),

  isInsufficientPermissionDialogVisible: createSelector(remoteControlState, (state: RemoteControlState) => state.insufficientPermissionDialogVisible),
  isGetServicePlanDialogVisible: createSelector(remoteControlState, (state: RemoteControlState) => state.getServicePlanDialogVisible),
  isExistingServicePlanSlotDialogVisible: createSelector(remoteControlState, (state: RemoteControlState) => state.existingServicePlanSlotDialog),
  isUpgradeServicePlanDialogVisible: createSelector(remoteControlState, (state: RemoteControlState) => state.upgradeServicePlanDialogVisible),

  hasEmptyServicePlanSlot: createSelector(remoteControlState, (state: RemoteControlState) => state?.cameraSlots > state?.assignedCamerasCount),
  hasUserServicePlan: createSelector(remoteControlState, (state: RemoteControlState) => state?.cameraSlots > 0),

  cameraMode: createSelector(remoteControlState, (state: RemoteControlState) => state.cameraModeOff),
  cameraModeOff: createSelector(remoteControlState, (state: RemoteControlState) => state.cameraModeOff),
  cameraModePending: createSelector(remoteControlState, (state: RemoteControlState) => state.cameraModePending),

  sensitivityPending: createSelector(remoteControlState, (state: RemoteControlState) => state.sensitivityPending),
  sensitivity: createSelector(remoteControlState, (state: RemoteControlState) => state.sensitivity),

  nightModePending: createSelector(remoteControlState, (state: RemoteControlState) => state.nightModePending),
  nightMode: createSelector(remoteControlState, (state: RemoteControlState) => state.nightMode),

  maxNumberPerHourPending: createSelector(remoteControlState, (state: RemoteControlState) => state.maxNumberPerHourPending),
  maxNumberPerHour: createSelector(remoteControlState, (state: RemoteControlState) => state.maxNumberPerHour),

  pictureNumberToSendPending: createSelector(remoteControlState, (state: RemoteControlState) => state.pictureNumberToSendPending),
  pictureNumberToSend: createSelector(remoteControlState, (state: RemoteControlState) => state.pictureNumberToSend ?? 1),

  pictureNumberToSendValueSet: createSelector(remoteControlState, (state: RemoteControlState) =>
    Array.from({ length: Math.min(state.snapshotNumber ?? 1, 5) }, (_, k) => k + 1)
  ),

  noPendingPhotoSettings: createSelector(remoteControlState, (state: RemoteControlState) => {
    return [
      state.snapshotNumberPending,
      state.quickPhotoEnabledPending,
      state.pictureNumberToSendPending,
      state.sendingPictureSizePending,
      state.imageSizePending,
    ].filter(Boolean).length;
  }),

  snapshotNumberPending: createSelector(remoteControlState, (state: RemoteControlState) => state.snapshotNumberPending),
  snapshotNumber: createSelector(remoteControlState, (state: RemoteControlState) => state.snapshotNumber),
  quickPhotoEnabled: createSelector(remoteControlState, (state: RemoteControlState) => state.quickPhotoEnabled),
  quickPhotoEnabledPending: createSelector(remoteControlState, (state: RemoteControlState) => state.quickPhotoEnabledPending),
  minimumSnapShotNumber: createSelector(remoteControlState, (state: RemoteControlState) => (state.quickPhotoEnabled ? 3 : 1)),
  maximumSnapShotNumber: createSelector(() => 5),

  sendingPictureSizePending: createSelector(remoteControlState, (state: RemoteControlState) => state.sendingPictureSizePending),

  imageSizePending: createSelector(remoteControlState, (state: RemoteControlState) => state.imageSizePending),
  imageSize: createSelector(remoteControlState, (state: RemoteControlState) => state.imageSize),

  timerSettingPending: createSelector(remoteControlState, (state: RemoteControlState) => state.timerSettingPending),
  timerSetting: createSelector(remoteControlState, (state: RemoteControlState) => state.timerSetting),
  timerSettingOff: createSelector(remoteControlState, (state: RemoteControlState) => state.timerSettingOff),
  activeMonitoringEnabled: createSelector(remoteControlState, (state: RemoteControlState) => !state.timerSettingOff),
  timerSettingParsed: createSelector(remoteControlState, (state: RemoteControlState) => parseTimerSetting(state.timerSetting)),

  activeMonitoringStartTime: createSelector(
    remoteControlState,
    (state: RemoteControlState) => state.activeMonitoringStartTime ?? parseTimeStringToDate('09:00')
  ),
  activeMonitoringEndTime: createSelector(remoteControlState, (state: RemoteControlState) => state.activeMonitoringEndTime ?? parseTimeStringToDate('21:00')),

  // capture settings: pause capture
  isPauseCaptureDelayEnabled: createSelector(remoteControlState, (state: RemoteControlState) => state.pauseCaptureDelayEnabled),
  isPauseCaptureDelayPending: createSelector(remoteControlState, (state: RemoteControlState) => state.pauseCaptureDelayPending),
  isUpdatingPauseCaptureDelay: createSelector(remoteControlState, (state: RemoteControlState) => state.updatingPauseCaptureDelay),
  isPauseCaptureDelayPickerShown: createSelector(remoteControlState, (state: RemoteControlState) => state.pauseCaptureDelayPickerShown),
  isTimelapseDurationPickerShown: createSelector(remoteControlState, (state: RemoteControlState) => state.timelapseDurationPickerShown),
  timePickerContext: createSelector(remoteControlState, (state: RemoteControlState) => state.timePickerContext),
  isTimePickerShown: createSelector(remoteControlState, (state: RemoteControlState) => state.timePickerContext !== null),
  getPauseCaptureDelay: createSelector(remoteControlState, (state: RemoteControlState) => state.pauseCaptureDelay),

  sendReportIntervalPending: createSelector(remoteControlState, (state: RemoteControlState) => state.sendReportIntervalPending),
  sendReportInterval: createSelector(remoteControlState, (state: RemoteControlState) => state.sendReportInterval),
  sendReportIntervalOff: createSelector(remoteControlState, (state: RemoteControlState) => state.sendReportIntervalOff),
  statusReportSliderIndex: createSelector(remoteControlState, state => getStatusReportSliderIndex(state.sendReportInterval)),
  sendReportIntervalDisplayLabel: createSelector(remoteControlState, state => getStatusReportDisplayLabel(state.sendReportInterval)),
  statusReportIntervalNumeric: createSelector(remoteControlState, (state: RemoteControlState) =>
    state.sendReportInterval ? Number.parseInt(state.sendReportInterval, 10) : 24
  ),
  isUpdatingStatusReport: createSelector(remoteControlState, state => state.isUpdatingStatusReport),

  instantModePending: createSelector(remoteControlState, (state: RemoteControlState) => state.instantModePending),
  instantMode: createSelector(remoteControlState, (state: RemoteControlState) => state.instantMode),
  instantModeOff: createSelector(remoteControlState, (state: RemoteControlState) => state.instantModeOff),

  cameraNameSettingPending: createSelector(remoteControlState, (state: RemoteControlState) => state.cameraNameSettingPending),
  cameraNameSetting: createSelector(remoteControlState, (state: RemoteControlState) => state.cameraNameSetting),
  cameraNameString: createSelector(remoteControlState, (state: RemoteControlState) => state.cameraNameString),
  cameraNameSettingOff: createSelector(remoteControlState, (state: RemoteControlState) => state.cameraNameSettingOff),

  secaTrackPending: createSelector(remoteControlState, (state: RemoteControlState) => state.secaTrackPending),
  secaTrack: createSelector(remoteControlState, (state: RemoteControlState) => state.secaTrack),
  secaTrackOff: createSelector(remoteControlState, (state: RemoteControlState) => state.secaTrackOff),

  gpsPending: createSelector(remoteControlState, (state: RemoteControlState) => state.gpsPending),
  gps: createSelector(remoteControlState, (state: RemoteControlState) => state.gps),
  gpsOff: createSelector(remoteControlState, (state: RemoteControlState) => state.gpsOff),

  sdCyclePending: createSelector(remoteControlState, (state: RemoteControlState) => state.sdCyclePending),
  sdCycle: createSelector(remoteControlState, (state: RemoteControlState) => state.sdCycle),
  sdCycleOff: createSelector(remoteControlState, (state: RemoteControlState) => state.sdCycleOff),

  pinCodePending: createSelector(remoteControlState, (state: RemoteControlState) => state.pinCodePending),
  resetPending: createSelector(remoteControlState, (state: RemoteControlState) => state.resetPending),

  updatingFlashMode: createSelector(remoteControlState, (state: RemoteControlState) => state.updatingFlashMode),
  flashModeError: createSelector(remoteControlState, (state: RemoteControlState) => state.flashModeError),

  isUpdatingMaxPhotos: createSelector(remoteControlState, (state: RemoteControlState) => state.updatingMaxPhotos),
  getMaxPhotosError: createSelector(remoteControlState, (state: RemoteControlState) => state.maxPhotosError),
  isLowLimitHintShown: createSelector(remoteControlState, (state: RemoteControlState) => state.lowLimitHintShown),

  isUpdatingActiveMonitoring: createSelector(remoteControlState, (state: RemoteControlState) => state.updatingActiveMonitoring),
  getActiveMonitoringError: createSelector(remoteControlState, (state: RemoteControlState) => state.activeMonitoringError),

  showAlwaysOnConfirmationAlert: createSelector(remoteControlState, (state: RemoteControlState) => state.showAlwaysOnConfirmationAlert),

  isSecacam1or3: createSelector(remoteControlState, (state: RemoteControlState) => (state.cameraModel ? isSecacam1or3(state.cameraModel) : false)),
  getPhotoSize: createSelector(remoteControlState, (state: RemoteControlState) => state.sendingPictureSize),

  getVideoSize: createSelector(remoteControlState, (state: RemoteControlState) => state.videoSize),
  isVideoSizeSectionEnabled: createSelector(remoteControlState, (state: RemoteControlState) => state.videoSizeSectionEnabled),
  isVideoSizePending: createSelector(remoteControlState, (state: RemoteControlState) => state.videoSizePending),
  isVideoSizeUpdating: createSelector(remoteControlState, (state: RemoteControlState) => state.videoSizeUpdating),

  getVideoTransmissionType: createSelector(remoteControlState, (state: RemoteControlState) => state.videoTransmissionType),
  isVideoTransmissionTypePending: createSelector(remoteControlState, (state: RemoteControlState) => state.videoTransmissionTypePending),
  isVideoTransmissionTypeUpdating: createSelector(remoteControlState, (state: RemoteControlState) => state.videoTransmissionTypeUpdating),

  isVideoTransmissionEnabled: createSelector(remoteControlState, (state: RemoteControlState) => state.videoTransmissionEnabled),
  isVideoTransmissionSectionEnabled: createSelector(remoteControlState, (state: RemoteControlState) => state.videoTransmissionSectionEnabled),
  isVideoTransmissionPending: createSelector(remoteControlState, (state: RemoteControlState) => state.videoTransmissionPending),
  isVideoTransmissionUpdating: createSelector(remoteControlState, (state: RemoteControlState) => state.videoTransmissionUpdating),

  isVideoRecordingEnabled: createSelector(remoteControlState, (state: RemoteControlState) => state.videoRecordingEnabled),
  isVideoRecordingPending: createSelector(remoteControlState, (state: RemoteControlState) => state.videoRecordingPending),
  isVideoRecordingUpdating: createSelector(remoteControlState, (state: RemoteControlState) => state.videoRecordingUpdating),

  getVideoLength: createSelector(remoteControlState, (state: RemoteControlState) => state.videoLength),
  isVideoLengthSectionEnabled: createSelector(remoteControlState, (state: RemoteControlState) => state.videoLengthSectionEnabled),
  isVideoLengthPending: createSelector(remoteControlState, (state: RemoteControlState) => state.videoLengthPending),
  isVideoLengthUpdating: createSelector(remoteControlState, (state: RemoteControlState) => state.videoLengthUpdating),

  timelapseSetting: createSelector(remoteControlState, state => state.timelapseSetting),
  motionSensorSetting: createSelector(remoteControlState, state => state.motionSensorSetting),
  timelapseSettingPending: createSelector(remoteControlState, state => state.timelapseSettingPending),
  timelapseSettingOff: createSelector(remoteControlState, state => state.timelapseSettingOff),
  timelapseEnabled: createSelector(remoteControlState, state => !state.timelapseSettingOff),
  updatingTimeLapse: createSelector(remoteControlState, state => state.timeLapseUpdating),
  isTimeLapseEnabled: createSelector(remoteControlState, state => state.timelapseSetting != undefined && state.timelapseSetting !== SETTING_OFF),
  isMotionSensorEnabled: createSelector(remoteControlState, state => state.motionSensorSetting === SETTING_ON),
  motionSensorEnabled: createSelector(remoteControlState, state => state.motionSensorSetting === SETTING_ON),
  timelapseDuration: createSelector(remoteControlState, state => state.timelapseDuration ?? DEFAULT_TIMELAPSE_DURATION),

  isApplyingControlPhotos: createSelector(remoteControlState, state => state.applyingControlPhotos),
  controlPicture1: createSelector(remoteControlState, (state: RemoteControlState) => state.controlPicture1),
  controlPicture1Off: createSelector(remoteControlState, (state: RemoteControlState) => state.controlPicture1Off),
  controlPicture1Enabled: createSelector(remoteControlState, (state: RemoteControlState) => !state.controlPicture1Off),
  controlPicture1Time: createSelector(remoteControlState, (state: RemoteControlState) => state.controlPicture1 ?? parseTimeStringToDate(DEFAULT_START_TIME)),
  controlPicture2Pending: createSelector(remoteControlState, (state: RemoteControlState) => state.controlPicture2Pending),
  controlPicture2: createSelector(remoteControlState, (state: RemoteControlState) => state.controlPicture2),
  controlPicture2Off: createSelector(remoteControlState, (state: RemoteControlState) => state.controlPicture2Off),
  controlPicture2Enabled: createSelector(remoteControlState, (state: RemoteControlState) => !state.controlPicture2Off),
  controlPicture2Time: createSelector(remoteControlState, (state: RemoteControlState) => state.controlPicture2 ?? parseTimeStringToDate(DEFAULT_START_TIME)),
  controlPicturePending: createSelector(remoteControlState, (state: RemoteControlState) => state.controlPicture1Pending || state.controlPicture2Pending),

  insufficientPermission: createSelector(CameraSelectors.isCameraViewer, viewer => viewer),
  operatorServicePlanNeeded: createSelector(
    remoteControlState,
    CameraSelectors.isCameraOperator,
    (state, operator) => state.paymentType === PaymentType.PREPAID && operator
  ),
  ownerServicePlanNeeded: createSelector(
    remoteControlState,
    CameraSelectors.isCameraOwner,
    (state, owner) => owner && state.paymentType === PaymentType.PREPAID && state.cameraSlots === 0
  ),
  existingServicePlanSlot: createSelector(
    remoteControlState,
    CameraSelectors.isCameraOwner,
    (state, owner) => owner && state.paymentType === PaymentType.PREPAID && state.cameraSlots > state.assignedCamerasCount
  ),
  upgradeServicePlanNeeded: createSelector(
    remoteControlState,
    CameraSelectors.isCameraOwner,
    (state, owner) => owner && state.paymentType === PaymentType.PREPAID && state.cameraSlots === state.assignedCamerasCount
  ),

  getNoPendingVideoSettings: createSelector(remoteControlState, (state: RemoteControlState) => {
    let noPendingItems = 0;
    if (state.videoRecordingPending) noPendingItems++;
    if (state.videoTransmissionPending) noPendingItems++;
    if (state.videoTransmissionTypePending) noPendingItems++;
    if (state.videoSizePending) noPendingItems++;
    if (state.videoLengthPending) noPendingItems++;
    return noPendingItems;
  }),

  recordingSectionPendingCount: createSelector(remoteControlState, (state: RemoteControlState) => {
    let count = 0;
    if (state.timerSettingPending) count++;
    if (state.timelapseSettingPending) count++;
    if (state.controlPicture1Pending || state.controlPicture2Pending) count++;
    if (state.sendReportIntervalPending) count++;
    return count;
  }),

  statusReportPopoverOpen: createSelector(remoteControlState, (state: RemoteControlState) => state.statusReportPopoverOpen),

  getNoPendingCaptureSettings: createSelector(remoteControlState, state => {
    let noPendingItems = 0;
    if (state.sensitivityPending) noPendingItems++;
    if (state.maxNumberPerHourPending) noPendingItems++;
    if (state.nightModePending) noPendingItems++;
    if (state.pauseCaptureDelayPending) noPendingItems++;
    return noPendingItems;
  }),

  isPhotoSettingsPending: createSelector(remoteControlState, state => false),

  // Updating flags for spinners
  isUpdatingQuickPhoto: createSelector(remoteControlState, (state: RemoteControlState) => state.updatingQuickPhoto),
  isUpdatingSnapshotNumber: createSelector(remoteControlState, (state: RemoteControlState) => state.updatingSnapshotNumber),
  isUpdatingPictureNumberToSend: createSelector(remoteControlState, (state: RemoteControlState) => state.updatingPictureNumberToSend),
  isUpdatingPhotoSize: createSelector(remoteControlState, (state: RemoteControlState) => state.updatingPhotoSize),
  isUpdatingImageSize: createSelector(remoteControlState, (state: RemoteControlState) => state.updatingImageSize),
  isUpdatingFooterCamName: createSelector(remoteControlState, (state: RemoteControlState) => state.updatingFooterCamName),
  isUpdatingAlwaysOn: createSelector(remoteControlState, (state: RemoteControlState) => state.updatingAlwaysOn),
  isUpdatingGps: createSelector(remoteControlState, (state: RemoteControlState) => state.updatingGps),
  isUpdatingSecaTrack: createSelector(remoteControlState, (state: RemoteControlState) => state.updatingSecaTrack),
  isUpdatingSdCycle: createSelector(remoteControlState, (state: RemoteControlState) => state.updatingSdCycle),
  isUpdatingPinCode: createSelector(remoteControlState, (state: RemoteControlState) => state.updatingPinCode),
  isUpdatingFactoryReset: createSelector(remoteControlState, (state: RemoteControlState) => state.updatingFactoryReset),
};

(RemoteControlSelectors as any).isPhotoSettingsPending = createSelector(RemoteControlSelectors.noPendingPhotoSettings, (noPending: number) => noPending > 0);

function getCurrentVideoSettingsState(videoSettings: VideoSettingsState, cameraModel: string): VideoSettingsState {
  return {
    videoRecordingEnabled: videoSettings.videoRecordingEnabled,
    videoRecordingPending: videoSettings.videoRecordingPending,

    videoTransmissionEnabled: videoSettings.videoTransmissionEnabled!,
    videoTransmissionPending: videoSettings.videoTransmissionPending,

    videoSizeSectionEnabled: checkIfCameraSupportsDifferentVideoSizes(cameraModel),
    videoSize: videoSettings.videoSize,
    videoSizePending: videoSettings.videoSizePending,

    videoTransmissionType:
      videoSettings.videoTransmissionType == VideoTransmissionType.NoVideoTransmission ? VideoTransmissionType.OnDemand : videoSettings.videoTransmissionType,
    videoTransmissionTypePending: videoSettings.videoTransmissionTypePending,
    videoTransmissionSectionEnabled: videoSettings.videoTransmissionEnabled,

    videoLength: Number.parseInt(videoSettings.videoLength as unknown as string, 10),
    videoLengthPending: videoSettings.videoLengthPending,
    videoLengthSectionEnabled: !videoSettings.videoTransmissionEnabled && videoSettings.videoRecordingEnabled,
  };
}
